/**
 * +rogue-specs: echoes (docs/design/classes/specs.md "Shadowdancer"). An aura with `echoes` turns
 * each of its stacks into a clone that repeats the holder's finisher a beat later:
 *
 *  - `queueEchoes` runs once per completed cast (applyEffects, after the 'castResolved' procs): for
 *    every aura on the caster whose echo rule lists the ability, one PendingEcho per stack lands
 *    `delayMs + i × stepMs` later; the aura is spent unless `consume: false`.
 *  - `tickEchoes` (start of every combat aura tick) resolves the due echoes: the echoed ability's
 *    damage effects only (conditional branches re-evaluated at landing, everything else skipped),
 *    scaled by `mult`, logged under `echoAbilityId`. A dead source or target makes the echo fizzle.
 *
 * Deterministic: echoes live in `world.echoes` (absent when empty, so worlds without clones stay
 * byte-identical) and resolve in queue order.
 */
import type { AbilityId, EffectCtx, EffectSpec, PendingEcho, SimContext } from '../contracts';
import { removeInstance } from './auras';
import { runEffects } from './effects';
import { unitOf } from './util';

const DEFAULT_STEP_MS = 150;

/** Only the damage of an effect list (conditionals kept, their branches filtered the same way). */
export function damageOnly(effects: readonly EffectSpec[]): EffectSpec[] {
  const out: EffectSpec[] = [];
  for (const e of effects) {
    if (e.type === 'damage') out.push(e);
    else if (e.type === 'conditional') {
      const then = damageOnly(e.then);
      const els = e.else ? damageOnly(e.else) : [];
      if (then.length || els.length) out.push({ ...e, then, ...(els.length ? { else: els } : {}) });
    }
  }
  return out;
}

/** After a completed cast: queue one echo per stack of every aura that echoes `abilityId`. */
export function queueEchoes(ctx: SimContext, ectx: EffectCtx): void {
  const s = unitOf(ctx, ectx.sourceId);
  if (!s?.alive) return;
  for (const inst of [...s.auras]) {
    const rules = ctx.content.aura(inst.auraId)?.echoes;
    if (!rules) continue;
    let spend = false;
    for (const r of rules) {
      if (!r.abilityIds.includes(ectx.abilityId)) continue;
      const step = r.stepMs ?? DEFAULT_STEP_MS;
      for (let i = 0; i < inst.stacks; i++) {
        (ctx.world.echoes ??= []).push({
          at: ctx.now + r.delayMs + i * step,
          sourceId: s.id,
          targetId: ectx.targetId,
          point: ectx.point,
          abilityId: ectx.abilityId,
          echoAbilityId: r.echoAbilityId,
          mult: r.mult,
        });
      }
      if (r.consume ?? true) spend = true;
    }
    if (spend && s.auras.includes(inst)) removeInstance(ctx, s, inst, 'consumed');
  }
}

/** Resolve every echo that is due (queue order). */
export function tickEchoes(ctx: SimContext): void {
  const q = ctx.world.echoes;
  if (!q?.length) return;
  const due: PendingEcho[] = [];
  const keep: PendingEcho[] = [];
  for (const e of q) (e.at <= ctx.now ? due : keep).push(e);
  if (!due.length) return;
  if (keep.length) ctx.world.echoes = keep;
  else delete ctx.world.echoes;
  for (const e of due) resolveEcho(ctx, e);
}

function resolveEcho(ctx: SimContext, e: PendingEcho): void {
  const s = unitOf(ctx, e.sourceId);
  if (!s?.alive) return;
  const t = e.targetId ? unitOf(ctx, e.targetId) : undefined;
  if (e.targetId && !t?.alive) return;
  const ab = ctx.content.ability(e.abilityId);
  if (!ab) return;
  const effects = damageOnly(ab.effects);
  if (!effects.length) return;
  runEffects(ctx, effects, { sourceId: s.id, abilityId: e.echoAbilityId as AbilityId, targetId: e.targetId, point: e.point, depth: 1 }, e.mult);
}
